"use client";

import { motion } from "framer-motion";
import { SectionEyebrow } from "@/features/shared/components/section-eyebrow";
import { ArrowRight } from "lucide-react";
import { useLeadCapture } from "@/features/shared/lib/lead-capture-context";
import { getProdutoIcone } from "@/features/shared/data/produtos-data";

interface LeadCaptureCTAProps {
  badge?: string;
  title?: string;
  subtitle?: string;
  /** De onde veio o lead — o slug da página, ex.: "motor-de-reservas" (sem o
      prefixo "form-", aplicado no onClick abaixo) — o mesmo valor usado
      pela hero da mesma página, pra "form-motor-de-reservas" ser o único
      parâmetro de rastreamento independente de qual botão da página abriu
      o modal. Também usado pra resolver o ícone do produto no modal (ver
      getProdutoIcone) — por isso precisa ser exatamente o slug da rota
      (sem a barra inicial), não um rótulo livre. */
  source: string;
  /** Texto curto do produto/página, usado no cabeçalho do modal ("Contato: {leadTitle}") — default "Foco Tecnologia". */
  leadTitle?: string;
}

/**
 * CTA de captura.
 *
 * A foto em parallax que servia de fundo foi removida — a seção usa apenas
 * a cor da superfície (#f4f7fb). Sem imagem e sem véu escuro, a hierarquia
 * passa a ser tipográfica: badge, título em display e botão na tinta da marca.
 */
function LeadCaptureCTA({
  badge = "Comece agora",
  title = "Pronto para transformar a gestão do seu hotel?",
  subtitle = "Solicite uma demonstração e descubra como a Foco pode otimizar todas as operações do seu negócio.",
  source,
  leadTitle,
}: LeadCaptureCTAProps) {
  const { openLeadCapture } = useLeadCapture();
  return (
    <section className="bg-[#f4f7fb] py-24 lg:py-28">
      <div className="container mx-auto px-6 lg:px-8 flex flex-col items-center text-center">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
          className="max-w-2xl"
        >
          {/* Badge */}
          <SectionEyebrow className="mb-7">{badge}</SectionEyebrow>

          {/* Title */}
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#132840] leading-none tracking-tighter antialiased mb-5">
            {title}
          </h2>

          {/* Subtitle */}
          <p className="text-lg text-[#4c5c73] font-light leading-relaxed mb-11 max-w-xl mx-auto">
            {subtitle}
          </p>

          {/* CTA button */}
          <motion.button
            type="button"
            onClick={() => openLeadCapture({ source: `form-${source}`, title: leadTitle, icon: getProdutoIcone(`/${source}`) })}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: "spring", stiffness: 360, damping: 24 }}
            className="inline-flex items-center gap-2.5 px-8 py-4 bg-gradient-to-t from-[#285992] to-[#427ab9] text-white text-sm font-semibold rounded-full shadow-lg shadow-[#285992]/25 hover:brightness-110 transition-all"
          >
            Solicite uma demonstração
            <ArrowRight className="w-4 h-4" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
}

export { LeadCaptureCTA };
